import {useEffect, useRef, useState} from "react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {isEqual} from "es-toolkit/compat";
import {isEmpty} from "es-toolkit/compat";
import {uniqBy} from "es-toolkit/compat";
import dayjs from "dayjs";

import {
  loadUser,
  loadMedicalInformation,
  checkMedicalInformation,
  checkUserDetails,
  updateUserDetails,
  loadCustomerVouchers,
  updateCustomerLogs,
} from "@/features/user";
import {loadSubscriptions, checkSubscriptions, updateSubscriptionsState} from "@/features/subscriptions";
import {
  loadOrders,
  checkCustomerOrders,
  updateCustomerOrders,
  loadPreBookOrders,
  checkPreBookOrders,
  updatePreBookOrders,
} from "@/features/orders";
import {loadBiomarkersInformation} from "@/features/biomarkers";
import {loadAllTests, loadAllPlusTests, checkAllPlusTests, updatePlusTestsState} from "@/features/tests";
import {loadPartnerDetails, checkPartnerDetails, updatePartnerDetails} from "@/features/partners";
import {
  loadResults,
  checkCustomerResults,
  updateCustomerResults,
  loadMarkerHistory,
  checkMarkerHistory,
  updateMarkerHistory,
  loadHealthCategories,
  checkHealthCategories,
  updateHealthCategoriesState,
  loadSupplementsRecommended,
  checkSupplementsRecommended,
  updateSupplementsState,
} from "@/features/results";
import {startSync, updateSyncStatus, syncDone, toggleLoadingScreen} from "@/features/sync";
// import {loadAllHealthProgrammes} from "@/features/healthProgrammes";
import {
  loadBlogs,
  checkBlogs,
  loadBlogsCategories,
  checkBlogsCategories,
  loadForYouBlogs,
  checkForYouBlogs,
  loadSavedPosts,
  updateLatestPostState,
  updateBlogsCategoriesState,
  updateForYouPostState,
} from "@/features/blogs";
// import {loadConsultations, checkConsultations, setConsultations} from "@/features/consultations";

import {addOneSignalData} from "@/helpers/addOneSignalData";
import {getWebsiteId} from "@/helpers/getWebsiteId";
import {getMedianToken} from "@/helpers/getMedianToken";
import {validateToken} from "@/helpers/validateToken";

import {selectIsOnline} from "@/features/appSettings/appSettingsSelectors";
import {selectIsAuthenticated} from "@/features/auth/authSelectors";
import {selectUserDetails} from "@/features/user/userSelectors";
import {selectBiomarkersData} from "@/features/biomarkers/biomarkersSelectors";
import {selectBlogsCategories, selectBlogsData} from "@/features/blogs/blogsSelectors";
import {selectOrdersData, selectPreBookOrdersData} from "@/features/orders/ordersSelector";
import {selectPartnersDetails} from "@/features/partners/partnersSelectors";
import {
  selectResultsData,
  selectResultsHealthCategories,
  selectResultsHistory,
  selectResultsMetadata,
  selectResultsSupplements,
} from "@/features/results/resultsSelectors";
import {selectSubscriptionsData} from "@/features/subscriptions/subscriptionsSelectors";
import {selectPlusTests, selectTestData} from "@/features/tests/testsSelectors";
import {selectSyncState} from "@/features/sync/syncSelectors";
import {logout} from "@/features/auth";
import {loadTestKitPricing} from "@/features/appSettings/appSettingsActions";

export const useSync = () => {
  const dataLoaded =
    localStorage.getItem("dataLoaded") == "false" ? false : localStorage.getItem("dataLoaded") == "true";
  const [isDataLoaded, setIsDataLoaded] = useState(dataLoaded);
  const [isSynchronising, setIsSynchronising] = useState(false);

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isOnline = useSelector(selectIsOnline);
  const userData = useSelector(selectUserDetails, shallowEqual);
  // const biomarkersData = useSelector(selectBiomarkersData);
  const resultsData = useSelector(selectResultsData, shallowEqual);
  const resultsMetadata = useSelector(selectResultsMetadata, shallowEqual);
  const resultsHistory = useSelector(selectResultsHistory, shallowEqual);
  const resultsHealthCategories = useSelector(selectResultsHealthCategories, shallowEqual);
  const resultsSupplements = useSelector(selectResultsSupplements, shallowEqual);
  // const consultations = useSelector(state => state.consultations?.data || []);
  const ordersData = useSelector(selectOrdersData, shallowEqual);
  const ordersPreBook = useSelector(selectPreBookOrdersData, shallowEqual);
  const subscriptionsData = useSelector(selectSubscriptionsData, shallowEqual);
  const partnersData = useSelector(selectPartnersDetails, shallowEqual);
  const blogsData = useSelector(selectBlogsData, shallowEqual);
  const blogsCategories = useSelector(selectBlogsCategories, shallowEqual);
  // const healthProgrammesData = useSelector(state => state.healthProgrammes?.data || []);
  // const testsData = useSelector(selectTestData);
  const testsPlusTests = useSelector(selectPlusTests, shallowEqual);
  const {loadingScreenMsg = "", showLoadingScreen = false} = useSelector(selectSyncState, shallowEqual);

  const isBespokeBuilder = ["partner-bespoke-test", "bespoke-test", "edit-test"].some(path =>
    window.location.pathname.includes(path),
  );

  const dispatch = useDispatch();
  const hasSyncFirstTime = useRef(false);

  useEffect(() => {
    const dataLoaded =
      localStorage.getItem("dataLoaded") == "false" ? false : localStorage.getItem("dataLoaded") == "true";
    setIsDataLoaded(dataLoaded);

    if (getWebsiteId() == 1 && !isAuthenticated) {
      getMedianToken();
    }
    if (isAuthenticated && isOnline && dataLoaded == false) {
      handleSyncAllData();
    }

    if (isAuthenticated && isOnline) {
      document.addEventListener("visibilitychange", detectAppVisibility);

      if (dataLoaded == true) hasSyncFirstTime.current = true;
    }

    return () => {
      if (isAuthenticated && isOnline) {
        document.removeEventListener("visibilitychange", detectAppVisibility);
      }
    };
  }, [isAuthenticated, isOnline]);

  useEffect(() => {
    let interval;

    if (isAuthenticated && isOnline && isDataLoaded == true) {
      interval = setInterval(() => {
        checkData();
      }, 600000);
    }

    return () => {
      if (isAuthenticated && isOnline && isDataLoaded == true) {
        interval && clearInterval(interval);
      }
    };
  }, [isAuthenticated, isOnline, isDataLoaded]);

  useEffect(() => {
    detectAppVisibility();
  }, []);

  useEffect(() => {
    if (isBespokeBuilder) {
      dispatch(loadTestKitPricing());
    }
  }, [isBespokeBuilder]);

  const handleSyncAllData = async () => {
    setIsSynchronising(true);
    dispatch(toggleLoadingScreen({show: true}));
    localStorage.setItem("lastSync", new Date().getTime());
    const websiteId = getWebsiteId();

    const isTokenValid = await validateToken();

    if (!isTokenValid) {
      dispatch(logout());
      return;
    }

    Promise.all([
      dispatch(loadUser({userId: "me", mustValidateToken: false})),
      dispatch(loadMedicalInformation({userId: "me", mustValidateToken: false})),
      dispatch(loadCustomerVouchers(false)),
      dispatch(loadOrders({userId: "me", mustValidateToken: false})),
      dispatch(loadPreBookOrders({userId: "me", mustValidateToken: false})),
      dispatch(loadMarkerHistory({userId: "me", mustValidateToken: false})),
      // dispatch(loadConsultations({userId: "me", mustValidateToken: false})),
      dispatch(loadResults({userId: "me", page: 1, mustValidateToken: false})),
      dispatch(loadHealthCategories({userId: "me", mustValidateToken: false})),
      dispatch(loadSupplementsRecommended({userId: "me", mustValidateToken: false})),
      dispatch(loadBiomarkersInformation({userId: "me", mustValidateToken: false})),
      // dispatch(loadBiomarkersAverage()),
      dispatch(loadAllTests()),
      dispatch(loadAllPlusTests(websiteId)),
      dispatch(loadSubscriptions({userId: "me", mustValidateToken: false})),
      // dispatch(loadAllHealthProgrammes(false)),
    ])
      .then(response => {
        if (response) {
          const [
            {payload: userResponse},
            {payload: medicalResponse},
            {payload: vouchersResponse},
            {payload: ordersResponse},
            {payload: resultsResponse},
            //{payload: consultationsResponse},
            {payload: healthCategoriesResponse},
            {payload: supplementsResponse},
            {payload: markerHistoryResponse},
            {payload: biomarkerInfoResponse},
            {payload: allTestResponse},
            {payload: allPlusTestsResponse},
            {payload: subscriptionsResponse},
            {payload: healthProgrammesResponse},
          ] = response || [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}];

          if (userResponse?.partnersAssigned?.length > 0) {
            const firstPartner = userResponse?.partnersAssigned?.find(partner => partner.access_status === "granted");
            firstPartner && dispatch(loadPartnerDetails(firstPartner.partner_id));
          }

          const data = {
            user: {...(userResponse?.payload || {}), ...(medicalResponse?.payload || {})},
            orders: ordersResponse?.payload || [],
            results: resultsResponse?.payload?.data || [],
            subscriptions: subscriptionsResponse?.payload || [],
          };

          addOneSignalData(data);
        }
      })
      .catch(e => {
        console.error(e);
        const payload = {
          logType: "AutomatedSystem",
          logMessage: `[ERROR] Last synced at: ${dayjs().format("DD/MM/YYYY HH:mm:ss")}, Error: ${e.message}`,
        };
        dispatch(updateCustomerLogs(payload));
      })
      .finally(() => {
        setIsSynchronising(false);
        dispatch(toggleLoadingScreen({show: false}));
        setIsDataLoaded(true);
        localStorage.setItem("dataLoaded", true);
        hasSyncFirstTime.current = true;

        const payload = {
          logType: "AutomatedSystem",
          logMessage: `Last synced at: ${dayjs().format("DD/MM/YYYY HH:mm:ss")}`,
        };

        dispatch(updateCustomerLogs(payload));
      });

    // Keep separate to not block the dashboard
    if (window.config.websiteId == 1 || window.config.websiteId == 2) {
      Promise.all([dispatch(loadBlogs()), dispatch(loadBlogsCategories())]).then(response => {
        const [{payload: blogsResponse}, {payload: categoriesResponse = []}] = response || [{}, {}];

        let categoriesPreferences;
        if (Array.isArray(categoriesResponse)) {
          categoriesPreferences = categoriesResponse
            ?.filter(cat => cat.isPreference == true)
            ?.map(cat => cat.id)
            ?.join(",");
        }

        if (categoriesPreferences) {
          dispatch(loadForYouBlogs({categoryIds: categoriesPreferences}));
        }
        dispatch(loadSavedPosts());
      });
    }

    return Promise.resolve();
  };

  const checkData = async () => {
    if (isBespokeBuilder) {
      return;
    }

    localStorage.setItem("lastSync", new Date().getTime());

    dispatch(
      startSync({
        userSync: false,
        ordersSync: false,
        resultsSync: false,
        // consultationsSync: false,
        markerHistorySync: false,
        healthCategoriesSync: false,
        supplementsSync: false,
        subscriptionSync: false,
        blogsSync: !(window.config.websiteId < 3),
      }),
    );

    const newUserData = await dispatch(checkUserDetails("me"));
    const newMedicalData = await dispatch(checkMedicalInformation("me"));
    const newCompleteUserData = {...(newUserData?.payload || {}), ...(newMedicalData?.payload || {})};
    dispatch(updateSyncStatus({userSync: true}));

    if (isEqual(userData, newCompleteUserData) === false) {
      dispatch(updateUserDetails(newCompleteUserData));
    }

    if (newUserData?.payload?.partnersAssigned?.length > 0) {
      const firstPartner = newUserData?.payload?.partnersAssigned?.find(partner => partner.access_status === "granted");

      if (firstPartner) {
        const newPartnerData = await dispatch(checkPartnerDetails(firstPartner.partner_id));
        if (isEqual(partnersData, newPartnerData?.payload) === false) {
          dispatch(updatePartnerDetails(newPartnerData?.payload));
        }
      }
    }

    const newOrders = await dispatch(checkCustomerOrders("me"));
    if (isEqual(ordersData, newOrders?.payload) === false) {
      dispatch(updateCustomerOrders(newOrders?.payload));
    }

    const newPreBookOrders = await dispatch(checkPreBookOrders("me"));
    if (isEqual(ordersPreBook, newPreBookOrders?.payload) === false) {
      dispatch(updatePreBookOrders(newPreBookOrders?.payload));
    }
    dispatch(updateSyncStatus({ordersSync: true}));

    const newResultsData = await dispatch(checkCustomerResults({userId: "me"}));
    dispatch(updateSyncStatus({resultsSync: true}));

    if (!newResultsData.payload.error) {
      const newResultsState = {
        data: uniqBy([...(newResultsData?.payload?.data || []), ...resultsData], "orderId"),
        metadata: {...resultsMetadata, ...(newResultsData?.payload?.metadata || {})},
      };

      // Check if a Result has being deleted and take it out from global state
      const resultIdsToRemove = [...(resultsData || [])]
        .slice(0, 5)
        .filter(result => {
          const testFound = newResultsData?.payload?.data?.find(r => r.orderId == result.orderId);

          return !testFound;
        })
        .map(result => result.orderId);

      if (!isEmpty(resultIdsToRemove)) {
        const newResultsStateData =
          newResultsState.data?.filter(result => !resultIdsToRemove.includes(result.orderId)) || [];

        newResultsState.data = newResultsStateData;
        newResultsState.metadata = {...(newResultsState.metadata || {}), currentPage: 1};
      }

      dispatch(updateCustomerResults(newResultsState));
    }

    // const newConsultationsData = await dispatch(checkConsultations("me"));
    // dispatch(updateSyncStatus({consultationsSync: true}));
    // if (isEqual(consultations?.data, newConsultationsData?.payload) === false) {
    //   dispatch(setConsultations(newConsultationsData?.payload));
    // }

    const newResultsHistory = await dispatch(checkMarkerHistory("me"));
    dispatch(updateSyncStatus({markerHistorySync: true}));
    if (!isEmpty(newResultsHistory?.payload) && isEqual(resultsHistory, newResultsHistory?.payload) === false) {
      dispatch(updateMarkerHistory(newResultsHistory?.payload));
    }

    const newResultHealthCategories = await dispatch(checkHealthCategories("me"));
    dispatch(updateSyncStatus({healthCategoriesSync: true}));
    if (isEqual(resultsHealthCategories, newResultHealthCategories?.payload) === false) {
      dispatch(updateHealthCategoriesState(newResultHealthCategories?.payload));
    }

    const newResultSupplements = await dispatch(checkSupplementsRecommended("me"));
    dispatch(updateSyncStatus({supplementsSync: true}));
    if (isEqual(resultsSupplements, newResultSupplements?.payload) === false) {
      dispatch(updateSupplementsState(newResultSupplements?.payload));
    }

    const newSubscriptions = await dispatch(checkSubscriptions("me"));
    dispatch(updateSyncStatus({subscriptionSync: true}));
    if (isEqual(subscriptionsData, newSubscriptions?.payload) === false) {
      dispatch(updateSubscriptionsState(newSubscriptions?.payload));
    }

    const newAllPlusTests = await dispatch(checkAllPlusTests(window.config.websiteId));
    if (!isEmpty(newAllPlusTests?.payload) && isEqual(testsPlusTests, newAllPlusTests?.payload) === false) {
      dispatch(updatePlusTestsState(newAllPlusTests?.payload));
    }

    if (window.config.websiteId < 3) {
      const newLatestBlogs = await dispatch(checkBlogs());
      if (isEqual(blogsData?.latestPosts?.results, newLatestBlogs?.payload?.data) === false) {
        dispatch(updateLatestPostState(newLatestBlogs?.payload));
      }

      const newBlogsCategories = await dispatch(checkBlogsCategories());
      if (isEqual(blogsCategories, newBlogsCategories?.payload) === false) {
        dispatch(updateBlogsCategoriesState(newBlogsCategories?.payload));
      }

      let categoriesPreferences;
      if (Array.isArray(newBlogsCategories?.payload)) {
        categoriesPreferences =
          newBlogsCategories?.payload
            ?.filter(cat => cat.isPreference == true)
            ?.map(cat => cat.id)
            ?.join(",") || "";
      }

      if (categoriesPreferences) {
        const newForYouBlogs = await dispatch(checkForYouBlogs({categoryIds: categoriesPreferences}));
        dispatch(updateSyncStatus({blogsSync: true}));
        if (isEqual(blogsData?.forYouPosts?.results, newForYouBlogs?.payload?.data) === false) {
          dispatch(updateForYouPostState(newForYouBlogs?.payload));
        }
      } else {
        dispatch(updateSyncStatus({blogsSync: true}));
      }
    }

    setTimeout(() => {
      dispatch(syncDone());
    }, 1000);

    const data = {
      user: newCompleteUserData,
      orders: newOrders?.payload || [],
      results: newResultsData?.payload || [],
      subscriptions: newSubscriptions?.payload || [],
    };

    addOneSignalData(data);

    const payload = {
      logType: "AutomatedSystem",
      logMessage: `Last synced at: ${dayjs().format("DD/MM/YYYY HH:mm:ss")}`,
    };

    dispatch(updateCustomerLogs(payload));
  };

  const detectAppVisibility = e => {
    const isVisible = document.visibilityState === "visible";
    if (!isAuthenticated || !isOnline) return;
    const hasLastSyncTime = localStorage.getItem("lastSync") !== null;

    if (isVisible && isOnline) {
      const lastSync = localStorage.getItem("lastSync");
      const lastSyncInMinutes = Math.floor((new Date().getTime() - lastSync) / 60000);

      if ((lastSyncInMinutes >= 10 || !hasLastSyncTime) && hasSyncFirstTime.current === true) {
        checkData();
      }
    }
  };

  return {isSynchronising, showLoadingScreen, loadingScreenMsg};
};
